import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import { Box, Grid } from '@mui/material';
import { Ellipsis } from 'lucide-react';
import { memo } from 'react';
import { Link } from 'react-router-dom';
import VirtualizedCardTableContent from './VirtualizedCardTableContent';
import VirtualizedCardTableSkeleton from './VirtualizedCardTableSkeleton';
import { BaseItem, VirtualizedItemTableProps } from './VirtualizedTable.types';
import useVirtualizedCardTable from './hooks/useVirtualizedCardTable';
import { useStyles } from './virtualizedTable.styles';
import { buildLinkTo, trackVisitedListItem } from './virtualizedTable.utils';

const VirtualizedCardTable = <T extends BaseItem>({
  item,
  onClickAction,
  columns,
  selectionProps,
  isFirstLoading,
  itemIndex,
  hrefState,
}: VirtualizedItemTableProps<T>) => {
  const { isSelected } = item;
  const { disableCheckBox, onToggleSelectRow } = selectionProps ?? {};
  const { classes } = useStyles();

  const { handleClickAction, mainColumn, chipColumns, bodyColumns, topRightElement } =
    useVirtualizedCardTable({ item, itemIndex, columns, isFirstLoading, onClickAction });

  if (isFirstLoading) {
    return <VirtualizedCardTableSkeleton columns={bodyColumns} />;
  }

  return (
    <>
      <Box
        sx={(theme) => ({
          display: 'flex',
          paddingInline: theme.spacing(0.25),
          paddingLeft: '2px',
          justifyContent: 'space-between',
          alignItems: 'center',
        })}
      >
        <Grid
          container
          spacing={1}
          sx={{
            alignItems: 'center',
            paddingInline: 0.5,
          }}
        >
          {!!onToggleSelectRow ? (
            <Grid>
              <Checkbox
                className={classes.disabledCheckbox}
                disabled={disableCheckBox}
                checked={isSelected}
                size="small"
                onClick={() => onToggleSelectRow(item)}
              />
            </Grid>
          ) : (
            <Grid style={{ width: '10px', height: '46px' }} />
          )}
          <Grid className={classes.mobileGridItemLink}>{mainColumn?.node}</Grid>
          {chipColumns.map((column) => (
            <Grid key={column.name} sx={{ display: 'flex' }}>
              {(column.cardRender ?? column.render)(item, itemIndex).node}
            </Grid>
          ))}
        </Grid>

        {!!onClickAction && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              columnGap: theme.spacing(1),
            })}
          >
            {topRightElement &&
              (topRightElement.cardRender ?? topRightElement.render)(item, itemIndex).node}
            <IconButton
              className={classes.actionsIconButton}
              size="medium"
              onClick={handleClickAction}
              icon={Ellipsis}
            />
          </Box>
        )}
      </Box>
      {mainColumn?.href ? (
        <Link
          to={buildLinkTo(mainColumn.href, hrefState)}
          className={classes.mobileLink}
          onClick={() => trackVisitedListItem(hrefState, item.id)}
        >
          <VirtualizedCardTableContent
            item={item}
            itemIndex={itemIndex}
            columns={bodyColumns}
            onClick={mainColumn.onClick}
          />
        </Link>
      ) : (
        <VirtualizedCardTableContent
          item={item}
          itemIndex={itemIndex}
          columns={bodyColumns}
          onClick={mainColumn?.onClick}
        />
      )}
    </>
  );
};

const MemoizedComponent = memo(VirtualizedCardTable) as typeof VirtualizedCardTable;

export default MemoizedComponent;
